import { supabase } from '@/integrations/supabase/client';
import { cloudinaryClient } from '@/integrations/cloudinary/direct-client';
import type { GeneratedImage } from '@/integrations/runware/types';
import type { ImageLibraryItem, SaveImageToLibraryParams } from '../types';

export interface SaveImageResult {
  success: boolean;
  imageLibraryId?: string;
  cloudinaryUrl?: string;
  error?: string;
}

/**
 * Save a Runware generated image to Cloudinary and database with full metadata
 */
export async function saveImageToStorage(
  image: GeneratedImage,
  title?: string,
  metadata?: {
    imageType?: string;
    styleId?: string;
    influenceId?: string;
    backgroundId?: string;
    environmentIds?: string[];
    moodIds?: string[];
    colorIds?: string[];
    width?: number;
    height?: number;
    cost?: number;
    fullPrompt?: string;
  }
): Promise<SaveImageResult> {
  const requestId = crypto.randomUUID();
  
  try {
    console.log(`[ImageSave] [${requestId}] Starting save process for image: ${image.imageUUID}`);
    console.log(`[ImageSave] [${requestId}] Metadata received:`, metadata);

    // Get current user
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      throw new Error('Authentication required');
    }

    // Download image from Runware
    const imageResponse = await fetch(image.imageURL);
    if (!imageResponse.ok) {
      throw new Error(`Failed to download image: ${imageResponse.status} ${imageResponse.statusText}`);
    }

    const imageBlob = await imageResponse.blob();
    console.log(`[ImageSave] [${requestId}] Downloaded image: ${imageBlob.size} bytes`);

    // Create file for upload
    const fileName = `generated-${image.imageUUID || Date.now()}.jpg`;
    const imageFile = new File([imageBlob], fileName, { type: 'image/jpeg' });

    // Upload to Cloudinary
    console.log(`[ImageSave] [${requestId}] Uploading to Cloudinary`);
    const uploadResult = await cloudinaryClient.uploadImage(imageFile);

    if (!uploadResult.success || !uploadResult.asset) {
      throw new Error(uploadResult.error || 'Cloudinary upload failed');
    }

    const { asset } = uploadResult;
    console.log(`[ImageSave] [${requestId}] Cloudinary upload successful: ${asset.publicId}`);

    // Save to database with full metadata
    const { data: imageLibraryData, error: dbError } = await supabase
      .from('image_library')
      .insert({
        user_id: session.user.id,
        image_url: asset.secureUrl,
        storage_path: asset.publicId,
        original_prompt: image.positivePrompt || '',
        description: title || `Generated Image ${new Date().toLocaleDateString()}`,
        width: metadata?.width || asset.width || 1024,
        height: metadata?.height || asset.height || 1024,
        seed: image.seed || null,
        favorite_count: 0,
        // Save all sidebar settings
        image_type: metadata?.imageType || null,
        style_id: metadata?.styleId || null,
        influence_id: metadata?.influenceId || null,
        background_id: metadata?.backgroundId || null,
        environment_ids: metadata?.environmentIds || null,
        mood_ids: metadata?.moodIds || null,
        color_ids: metadata?.colorIds || null,
        cost: metadata?.cost || 0,
        full_prompt: metadata?.fullPrompt || ''
      })
      .select('id')
      .single();

    if (dbError) {
      console.error(`[ImageSave] [${requestId}] Database save failed:`, dbError);
      throw new Error(`Database save failed: ${dbError.message}`);
    }

    console.log(`[ImageSave] [${requestId}] Save completed successfully`);
    return {
      success: true,
      imageLibraryId: imageLibraryData.id,
      cloudinaryUrl: asset.secureUrl
    };

  } catch (error) {
    console.error(`[ImageSave] [${requestId}] Save failed:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save image'
    };
  }
}

/**
 * Legacy wrapper for backward compatibility - now properly saves all sidebar settings
 */
export async function saveImageToLibrary(params: SaveImageToLibraryParams): Promise<ImageLibraryItem | null> {
  try {
    // Check authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) {
      console.error('[saveImageToLibrary] Authentication required');
      return null;
    }
    
    const { 
      generatedImage, 
      description, 
      imageType, 
      styleId, 
      influenceId, 
      backgroundId, 
      environmentIds, 
      moodIds, 
      colorIds, 
      width, 
      height,
      cost,
      fullPrompt
    } = params;
    
    // Convert legacy format to new format
    const adaptedImage: GeneratedImage = {
      imageURL: generatedImage.imageURL,
      imageUUID: generatedImage.imageUUID,
      positivePrompt: generatedImage.positivePrompt,
      seed: generatedImage.seed || 0,
      NSFWContent: false,
      cost: 0
    };
    
    // Prepare metadata with all sidebar settings
    const metadata = {
      imageType,
      styleId: styleId || undefined,
      influenceId: influenceId || undefined,
      backgroundId: backgroundId || undefined,
      environmentIds: environmentIds && environmentIds.length > 0 ? environmentIds : undefined,
      moodIds: moodIds && moodIds.length > 0 ? moodIds : undefined,
      colorIds: colorIds && colorIds.length > 0 ? colorIds : undefined,
      width,
      height,
      cost: cost || 0,
      fullPrompt: fullPrompt || generatedImage.positivePrompt || ''
    };
    
    console.log('[saveImageToLibrary] Saving with metadata:', metadata);
    
    // Save using new function with full metadata
    const result = await saveImageToStorage(adaptedImage, description, metadata);
    
    if (!result.success) {
      console.error('[saveImageToLibrary] Save failed:', result.error);
      return null;
    }
    
    // Return saved image data
    const { data: savedImage, error } = await supabase
      .from('image_library')
      .select('*')
      .eq('id', result.imageLibraryId)
      .single();
    
    if (error) {
      console.error('[saveImageToLibrary] Failed to fetch saved image:', error);
      return null;
    }
    
    return savedImage;
    
  } catch (error) {
    console.error('[saveImageToLibrary] Error:', error);
    return null;
  }
}

/**
 * Delete an image from the library (database only - Cloudinary cleanup handled by lifecycle policies)
 */
export async function deleteImageFromStorage(imageId: string): Promise<{ success: boolean; error?: string }> {
  try {
    console.log(`[ImageDelete] Deleting image: ${imageId}`);

    // Delete from database
    const { error: deleteError } = await supabase
      .from('image_library')
      .delete()
      .eq('id', imageId);

    if (deleteError) {
      throw new Error(`Failed to delete from database: ${deleteError.message}`);
    }

    console.log(`[ImageDelete] Image deleted successfully: ${imageId}`);
    return { success: true };

  } catch (error) {
    console.error(`[ImageDelete] Delete failed:`, error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete image'
    };
  }
}

/**
 * Legacy wrapper for deleteImageFromStorage
 */
export async function deleteImageFromLibrary(imageId: string): Promise<void> {
  const result = await deleteImageFromStorage(imageId);
  if (!result.success) {
    throw new Error(result.error || 'Failed to delete image');
  }
}
